import React, { useState, useEffect } from 'react';
import { getSucursalById, createSucursal, updateSucursal } from '../services/sucursalService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, Landmark, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function SucursalForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        codigoEstablecimiento: '',
        direccion: '',
        telefono: '',
        departamentoId: '',
        ciudadId: '',
        esCasaCentral: false,
        activo: true
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);

    const [departamentos, setDepartamentos] = useState([]);
    const [ciudades, setCiudades] = useState([]);

    useEffect(() => {
        const fetchDepartamentos = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/departamentos`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await response.json();
                setDepartamentos(data);
            } catch (error) {
                console.error('Error fetching departamentos', error);
            }
        };
        fetchDepartamentos();
    }, [token]);

    useEffect(() => {
        if (formData.departamentoId) {
            const fetchCiudades = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/ciudades/por-departamento/${formData.departamentoId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const data = await response.json();
                    setCiudades(data);
                } catch (error) {
                    console.error('Error fetching ciudades', error);
                }
            };
            fetchCiudades();
        } else {
            setCiudades([]);
        }
    }, [formData.departamentoId, token]);

    useEffect(() => {
        if (isEditMode) {
            cargarSucursal();
        }
    }, [id]);

    const cargarSucursal = async () => {
        try {
            const data = await getSucursalById(id);
            // Transformar para el formulario
            setFormData({
                ...data,
                departamentoId: data.departamento?.id || '',
                ciudadId: data.ciudad?.id || ''
            });
        } catch (error) {
            toast.error('Error al cargar sucursal');
            navigate('/parametrizacion/sucursales');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            
            // Estructura requerida por JPA: objeto con id
            const payload = {
                ...formData,
                departamento: formData.departamentoId ? { id: formData.departamentoId } : null,
                ciudad: formData.ciudadId ? { id: formData.ciudadId } : null
            };

            if (isEditMode) {
                await updateSucursal(id, payload);
                toast.success('Sucursal actualizada');
            } else {
                await createSucursal(payload);
                toast.success('Sucursal creada con éxito');
            }
            setTimeout(() => navigate('/parametrizacion/sucursales'), 1000);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <div className="p-20 text-center uppercase font-black text-slate-400 tracking-[0.2em]">Cargando Sucursal...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto bg-white shadow-xl border border-slate-200 mb-10 overflow-hidden font-sans">
            <Toaster position="top-right" />
            <div className="bg-slate-700 p-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/parametrizacion/sucursales')} className="p-2 hover:bg-slate-600 rounded-full transition-all" type="button">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-bold uppercase tracking-tight">
                        {isEditMode ? 'Editar Sucursal' : 'Nueva Sucursal'}
                    </h1>
                </div>
                <Landmark className="w-10 h-10 text-emerald-500 opacity-30" />
            </div>

            <form onSubmit={handleSubmit} className="p-10 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Nombre de la Sucursal</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase text-slate-800 focus:border-emerald-500 outline-none"
                            value={formData.nombre}
                            onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Código Establecimiento (SET)</label>
                        <input
                            type="text"
                            required
                            maxLength="3"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold text-blue-600 focus:border-emerald-500 outline-none"
                            placeholder="001"
                            value={formData.codigoEstablecimiento}
                            onChange={(e) => setFormData({ ...formData, codigoEstablecimiento: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Teléfono de Contacto</label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-medium focus:border-emerald-500 outline-none"
                            value={formData.telefono}
                            onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Dirección Física</label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-medium focus:border-emerald-500 outline-none"
                            value={formData.direccion}
                            onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Departamento</label>
                        <select
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase focus:border-emerald-500 outline-none"
                            value={formData.departamentoId}
                            onChange={(e) => setFormData({ ...formData, departamentoId: e.target.value, ciudadId: '' })}
                        >
                            <option value="">Seleccione Departamento</option>
                            {departamentos.map(d => (
                                <option key={d.id} value={d.id}>{d.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Ciudad</label>
                        <select
                            required
                            disabled={!formData.departamentoId}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase focus:border-emerald-500 outline-none disabled:opacity-50"
                            value={formData.ciudadId}
                            onChange={(e) => setFormData({ ...formData, ciudadId: e.target.value })}
                        >
                            <option value="">Seleccione Ciudad</option>
                            {ciudades.map(c => (
                                <option key={c.id} value={c.id}>{c.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-sm border border-slate-100">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="esCasaCentral"
                                className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                                checked={formData.esCasaCentral}
                                onChange={(e) => setFormData({ ...formData, esCasaCentral: e.target.checked })}
                            />
                            <label htmlFor="esCasaCentral" className="text-xs font-bold text-slate-700 uppercase">¿Es Casa Central?</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="activo"
                                className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                                checked={formData.activo}
                                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                            />
                            <label htmlFor="activo" className="text-xs font-bold text-slate-700 uppercase">Registro Activo</label>
                        </div>
                    </div>
                </div>

                <div className="pt-8 border-t flex justify-end gap-4">
                    <button type="button" onClick={() => navigate('/parametrizacion/sucursales')} className="text-xs font-black uppercase text-slate-400 hover:text-slate-600">DESCARTAR</button>
                    <button type="submit" disabled={loading} className="px-10 py-3 bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-500" />}
                        {isEditMode ? 'ACTUALIZAR' : 'GUARDAR SUCURSAL'}
                    </button>
                </div>
            </form>
        </div>
    );
}
